import React, { useEffect, useState } from "react";
import {
  Competition,
  CompetitionPrice,
  MyTeamCompetitions,
} from "../../types/competition.types";
import { useCompetitions } from "../../context/CompetitionsContext";
import { Button } from "../../components/button";
import {
  CalendarIcon,
  ClockIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/24/outline";
import { Outlet, useLocation } from "react-router-dom";
import { TeamCompetitionStatus } from "../../types/team_competitions.types";
import { DATE_TIME_WEEKDAY_LONG_24H } from "../../utils/date-formats";
import { CreditCardIcon } from "@heroicons/react/24/solid";
import { ErrorMessage } from "../../components/error-message";
import { Loading } from "../../components/loading";
import bgPayment from "../../assets/bgPayment.jpg";
import { CheckCircle, Circle, Trophy } from "lucide-react";
import Label from "../../components/label";

interface ICompetitionsUsersBaseProps {
  onRegister?: (competition: Competition) => void;
  onPayment?: (competition: Competition, price: CompetitionPrice) => void;
  isGettingPaymentLink?: boolean;
}

export const CompetitionsUsersBase: React.FC<ICompetitionsUsersBaseProps> = ({
  onRegister,
  onPayment,
  isGettingPaymentLink,
}) => {
  const {
    availableCompetitions,
    registeredCompetitions,
    error,
    isLoading,
    getCompetitions,
  } = useCompetitions();
  const location = useLocation();
  const [selectedPriceIndices, setSelectedPriceIndices] = useState<{
    [competitionId: number]: number;
  }>({});

  const isListView = location.pathname === "/dashboard/competitions";

  const getLatestPaymentDeadline = (currentPrices: any[]) => {
    if (!currentPrices || currentPrices.length === 0) return new Date();

    return new Date(
      currentPrices.reduce((latest, price) => {
        const endDate = new Date(price.end_date);
        return endDate > new Date(latest) ? price.end_date : latest;
      }, currentPrices[0].end_date)
    );
  };

  const isPaymentDeadlinePassed = (currentPrices: any[]) => {
    if (!currentPrices || currentPrices.length === 0) return true;
    const latestDeadline = getLatestPaymentDeadline(currentPrices);
    return new Date() > latestDeadline;
  };

  const formatCompetitionDate = (dateString: string | Date) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString("es-AR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }),
      time: date.toLocaleTimeString("es-AR", {
        hour: "2-digit",
        minute: "2-digit",
      }),
      full: date.toLocaleString("es-AR", DATE_TIME_WEEKDAY_LONG_24H),
    };
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("es-AR", {
      style: "currency",
      currency: "ARS",
      minimumFractionDigits: 0,
    }).format(price);
  };

  const isPriceOptionValid = (startDate: string, endDate: string) => {
    const today = new Date();
    const start = new Date(startDate);
    const end = new Date(endDate);
    return today >= start && today <= end;
  };

  useEffect(() => {
    getCompetitions();
  }, [getCompetitions]);

  useEffect(() => {
    const initialSelectedIndices: { [competitionId: number]: number } = {};

    registeredCompetitions.forEach((competition) => {
      if (competition.prices && competition.prices.length > 0) {
        const sortedPrices = competition.prices
          .map((priceOption, originalIndex) => ({
            ...priceOption,
            originalIndex,
          }))
          .sort((a, b) => {
            const aIsValid = isPriceOptionValid(a.start_date, a.end_date);
            const bIsValid = isPriceOptionValid(b.start_date, b.end_date);

            if (aIsValid && !bIsValid) return -1;
            if (!aIsValid && bIsValid) return 1;

            const aIsTransfer = a.payment_type === "transfer";
            const bIsTransfer = b.payment_type === "transfer";

            if (aIsTransfer && !bIsTransfer) return -1;
            if (!aIsTransfer && bIsTransfer) return 1;

            return a.originalIndex - b.originalIndex;
          });

        const validPriceIndex = sortedPrices.findIndex((price) =>
          isPriceOptionValid(price.start_date, price.end_date)
        );

        initialSelectedIndices[competition.id] =
          validPriceIndex >= 0
            ? sortedPrices[validPriceIndex].originalIndex
            : sortedPrices[0]?.originalIndex || 0;
      }
    });

    setSelectedPriceIndices(initialSelectedIndices);
  }, [registeredCompetitions]);

  const handlePriceSelection = (competitionId: number, priceIndex: number) => {
    setSelectedPriceIndices((prev) => ({
      ...prev,
      [competitionId]: priceIndex,
    }));
  };

  return (
    <div
      className="flex min-h-screen items-center justify-center bg-cover bg-center relative bg-fixed overflow-hidden"
      style={{ backgroundImage: `url(${bgPayment})` }}
    >
      <div className="absolute inset-0 bg-black opacity-50 z-0" />{" "}
      <div className="relative w-full sm:max-w-lg lg:max-w-xl p-8 bg-[#F8F9FA] shadow-xl rounded-2xl bg-opacity-80 text-gray-900 border border-gray-300 z-10 my-4">
        {isListView ? (
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="flex items-center space-x-2 mb-6 justify-center">
              <Trophy className="h-6 w-6 text-gray-800" />
              <h1 className="text-2xl font-bold text-gray-800">Competencia</h1>
            </div>

            {error && (
              <div className="mb-4">
                <ErrorMessage message={error} dismissible />
              </div>
            )}

            {isLoading ? (
              <Loading />
            ) : (
              <>
                {availableCompetitions.length === 0 &&
                  registeredCompetitions.length === 0 && (
                    <p className="text-center text-gray-500">
                      No hay competencias disponibles o registradas.
                    </p>
                  )}

                {registeredCompetitions.length > 0 && (
                  <div className="mb-4">
                    <div className="grid gap-4">
                      {registeredCompetitions.map((competition) => {
                        const latestPaymentDeadline = getLatestPaymentDeadline(
                          competition.prices || []
                        );
                        const paymentDeadline = formatCompetitionDate(
                          latestPaymentDeadline
                        );
                        const deadlinePassed = isPaymentDeadlinePassed(
                          competition.prices || []
                        );
                        const selectedPriceIndex =
                          selectedPriceIndices[competition.id];

                        return (
                          <div
                            key={competition.id}
                            className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow"
                          >
                            <h3 className="text-xl font-bold mb-4 text-blue-600 flex items-center">
                              {competition.name}
                            </h3>

                            <div className="space-y-2 text-sm text-gray-600">
                              <p className="flex items-center">
                                <CalendarIcon className="w-4 h-4 mr-2" />
                                <strong className="mr-1">
                                  Inicio de la competencia:
                                </strong>
                                {
                                  formatCompetitionDate(competition.start_date)
                                    .full
                                }
                              </p>
                              <p className="flex items-center">
                                <CalendarIcon className="w-4 h-4 mr-2" />
                                <strong className="mr-1">
                                  Fin de la competencia:
                                </strong>
                                {
                                  formatCompetitionDate(competition.end_date)
                                    .full
                                }
                              </p>
                              <p
                                className={`flex items-center ${
                                  deadlinePassed
                                    ? "text-red-600"
                                    : "text-gray-600"
                                }`}
                              >
                                <ClockIcon className="w-4 h-4 mr-2" />
                                <strong className="mr-1">
                                  Fecha límite para pagar:
                                </strong>
                                <span
                                  className={
                                    deadlinePassed ? "line-through" : ""
                                  }
                                >
                                  {paymentDeadline.full}
                                </span>
                                {deadlinePassed && (
                                  <span className="ml-2">(Expirado)</span>
                                )}
                              </p>
                            </div>

                            <div className="mt-4">
                              <div className="flex items-center space-x-2">
                                <span className="text-sm font-semibold text-gray-700">
                                  Estado:
                                </span>

                                {(() => {
                                  if (competition.payment_status === "exempt") {
                                    return (
                                      <div className="flex items-center">
                                        <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">
                                          Exento de Pago
                                        </span>
                                        {competition.team_number && (
                                          <span className="ml-2 px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
                                            N° de equipo: #
                                            {competition.team_number}
                                          </span>
                                        )}
                                      </div>
                                    );
                                  }

                                  const isAcceptedAndPaidOrExempt = (
                                    status: TeamCompetitionStatus,
                                    paymentStatus: MyTeamCompetitions["payment_status"]
                                  ) => {
                                    return (
                                      status ===
                                        TeamCompetitionStatus.Accepted &&
                                      (paymentStatus === "paid" ||
                                        paymentStatus === "exempt")
                                    );
                                  };

                                  if (
                                    isAcceptedAndPaidOrExempt(
                                      competition.status,
                                      competition.payment_status
                                    )
                                  ) {
                                    return (
                                      <div className="flex items-center">
                                        <Label color="green" fontSize="sm">
                                          {" "}
                                          Pagado
                                        </Label>
                                        {competition.team_number && (
                                          <Label color="gray" fontSize="sm">
                                            {" "}
                                            N° de equipo: #{" "}
                                            {competition.team_number}
                                          </Label>
                                        )}
                                      </div>
                                    );
                                  }

                                  if (
                                    competition.payment_status === "paid" &&
                                    competition.status ===
                                      TeamCompetitionStatus.Registered
                                  ) {
                                    return (
                                      <Label color="blue" fontSize="sm">
                                        {" "}
                                        Pagado - Pendiente de Aceptación
                                      </Label>
                                    );
                                  }

                                  if (
                                    competition.payment_status === "unpaid" &&
                                    competition.status ===
                                      TeamCompetitionStatus.Registered
                                  ) {
                                    return (
                                      <Label color="blue" fontSize="sm">
                                        {" "}
                                        Registrado
                                      </Label>
                                    );
                                  }

                                  if (
                                    competition.status ===
                                      TeamCompetitionStatus.PaymentInProgress ||
                                    competition.payment_status === "pending"
                                  ) {
                                    return (
                                      <Label color="blue" fontSize="sm">
                                        {" "}
                                        Pago en Progreso
                                      </Label>
                                    );
                                  }

                                  if (
                                    competition.status ===
                                    TeamCompetitionStatus.Registered
                                  ) {
                                    return (
                                      <Label color="blue" fontSize="sm">
                                        {" "}
                                        Registrado
                                      </Label>
                                    );
                                  }

                                  return (
                                    <Label color="gray" fontSize="sm">
                                      {" "}
                                      Estado Desconocido
                                    </Label>
                                  );
                                })()}
                              </div>
                              {(competition.status ===
                                TeamCompetitionStatus.PaymentInProgress ||
                                competition.payment_status === "pending") && (
                                <div className="text-s text-gray-500 mt-2 italic">
                                  <p>Tu pago está siendo procesado.</p>
                                  <ul className="list-disc list-inside mt-1">
                                    <li>
                                      Si realizaste una transferencia, será
                                      verificada por un administrador.
                                    </li>
                                    <li>
                                      Si pagaste por MercadoPago, la
                                      confirmación puede demorar unos minutos.
                                    </li>
                                  </ul>
                                </div>
                              )}

                              {competition.status ===
                                TeamCompetitionStatus.Registered &&
                                competition.payment_status === "unpaid" &&
                                onPayment &&
                                competition.prices &&
                                competition.prices.length > 0 && (
                                  <div className="mt-4">
                                    <h4 className="text-sm font-semibold text-gray-700 mb-2">
                                      Opciones de pago:
                                    </h4>

                                    <div className="space-y-3 mb-4">
                                      {competition.prices
                                        .map((priceOption, originalIndex) => ({
                                          ...priceOption,
                                          originalIndex,
                                        }))
                                        .sort((a, b) => {
                                          const aIsValid = isPriceOptionValid(
                                            a.start_date,
                                            a.end_date
                                          );
                                          const bIsValid = isPriceOptionValid(
                                            b.start_date,
                                            b.end_date
                                          );

                                          if (aIsValid && !bIsValid) return -1;
                                          if (!aIsValid && bIsValid) return 1;

                                          const aIsTransfer =
                                            a.payment_type === "transfer";
                                          const bIsTransfer =
                                            b.payment_type === "transfer";

                                          if (aIsTransfer && !bIsTransfer)
                                            return -1;
                                          if (!aIsTransfer && bIsTransfer)
                                            return 1;

                                          return (
                                            a.originalIndex - b.originalIndex
                                          );
                                        })
                                        .map((priceOption, index) => {
                                          const isValid = isPriceOptionValid(
                                            priceOption.start_date,
                                            priceOption.end_date
                                          );
                                          const isFuture =
                                            new Date(priceOption.start_date) >
                                            new Date();

                                          const getPaymentTypeInfo = (
                                            paymentType: string
                                          ) => {
                                            switch (paymentType) {
                                              case "mercadopago":
                                                return {
                                                  label: "MercadoPago",
                                                  color: "text-blue-600",
                                                  bgColor: "bg-blue-50",
                                                  borderColor:
                                                    "border-blue-200",
                                                  showIVA: true,
                                                  icon: "💳",
                                                };
                                              case "transfer":
                                                return {
                                                  label: "Transferencia",
                                                  color: "text-orange-600",
                                                  bgColor: "bg-orange-50",
                                                  borderColor:
                                                    "border-orange-200",
                                                  showIVA: false,
                                                  icon: "🏦",
                                                };
                                              case "cash":
                                                return {
                                                  label: "Efectivo",
                                                  color: "text-green-600",
                                                  bgColor: "bg-green-50",
                                                  borderColor:
                                                    "border-green-200",
                                                  showIVA: false,
                                                  icon: "💵",
                                                };
                                              default:
                                                return {
                                                  label: "Otro",
                                                  color: "text-gray-600",
                                                  bgColor: "bg-gray-50",
                                                  borderColor:
                                                    "border-gray-200",
                                                  showIVA: true,
                                                  icon: "💰",
                                                };
                                            }
                                          };

                                          const paymentInfo =
                                            getPaymentTypeInfo(
                                              priceOption.payment_type
                                            );

                                          return (
                                            <div
                                              key={priceOption.originalIndex}
                                              onClick={() => {
                                                if (isValid)
                                                  handlePriceSelection(
                                                    competition.id,
                                                    priceOption.originalIndex
                                                  );
                                              }}
                                              className={`
            p-4 border rounded-lg transition-all relative
            ${
              isValid
                ? "cursor-pointer hover:border-blue-300"
                : "cursor-not-allowed"
            }
            ${
              selectedPriceIndex === priceOption.originalIndex
                ? "border-blue-500 bg-blue-50"
                : "border-gray-200"
            }
            ${!isValid && "opacity-60"}
            ${
              priceOption.payment_type === "transfer"
                ? "ring-1 ring-orange-200"
                : ""
            }
          `}
                                            >
                                              <div className="absolute top-2 right-2">
                                                <span
                                                  className={`
                inline-flex items-center px-2 py-1 rounded-full text-xs font-medium
                ${paymentInfo.color} ${paymentInfo.bgColor} ${paymentInfo.borderColor} border
              `}
                                                >
                                                  <span className="mr-1">
                                                    {paymentInfo.icon}
                                                  </span>
                                                  {paymentInfo.label}
                                                </span>
                                              </div>

                                              <div className="flex items-center justify-between pr-20">
                                                <div className="flex flex-col">
                                                  <p className="font-medium text-lg">
                                                    {formatPrice(
                                                      priceOption.price
                                                    )}
                                                    {paymentInfo.showIVA && (
                                                      <span className="text-sm text-gray-500 ml-1">
                                                        + IVA
                                                      </span>
                                                    )}
                                                  </p>

                                                  {priceOption.payment_type ===
                                                    "transfer" && (
                                                    <p className="text-xs text-gray-600 mt-1">
                                                      Sin comisiones adicionales
                                                    </p>
                                                  )}

                                                  {priceOption.payment_type ===
                                                    "mercadopago" && (
                                                    <p className="text-xs text-gray-600 mt-1">
                                                      Procesado por MercadoPago
                                                    </p>
                                                  )}

                                                  {priceOption.payment_type ===
                                                    "cash" && (
                                                    <p className="text-xs text-gray-600 mt-1">
                                                      Pago en efectivo
                                                      presencial
                                                    </p>
                                                  )}

                                                  <p className="text-xs text-gray-500 mt-2">
                                                    {isFuture
                                                      ? "Válido desde:"
                                                      : "Válido hasta:"}{" "}
                                                    {new Date(
                                                      isFuture
                                                        ? priceOption.start_date
                                                        : priceOption.end_date
                                                    ).toLocaleDateString(
                                                      "es-AR"
                                                    )}
                                                  </p>

                                                  {priceOption.installment_count >
                                                    1 && (
                                                    <p className="text-xs text-green-600 mt-1">
                                                      Hasta{" "}
                                                      {
                                                        priceOption.installment_count
                                                      }{" "}
                                                      cuotas disponibles
                                                    </p>
                                                  )}
                                                </div>

                                                <div
                                                  className={`
                ml-4 
                ${
                  selectedPriceIndex === priceOption.originalIndex
                    ? "text-blue-500"
                    : "text-gray-400"
                }
              `}
                                                >
                                                  {selectedPriceIndex ===
                                                  priceOption.originalIndex ? (
                                                    <CheckCircle
                                                      className="text-blue-500"
                                                      size={20}
                                                    />
                                                  ) : (
                                                    <Circle
                                                      className="text-gray-300"
                                                      size={20}
                                                      strokeWidth={1.5}
                                                    />
                                                  )}
                                                </div>
                                              </div>
                                            </div>
                                          );
                                        })}
                                    </div>

                                    {deadlinePassed ? (
                                      <div className="p-3 bg-red-100 border border-red-200 rounded-lg flex items-center">
                                        <ExclamationCircleIcon className="w-5 h-5 mr-2 text-red-600" />
                                        <p className="text-red-600 text-sm">
                                          ❌ El plazo de pago ha expirado. Ya no
                                          es posible realizar el pago.
                                        </p>
                                      </div>
                                    ) : (
                                      <Button
                                        onClick={() => {
                                          if (
                                            competition.prices &&
                                            competition.prices.length > 0
                                          ) {
                                            onPayment(
                                              competition,
                                              competition.prices[
                                                selectedPriceIndex
                                              ]
                                            );
                                          }
                                        }}
                                        text="Pagar inscripción"
                                        variant="create"
                                        disabled={
                                          isGettingPaymentLink ||
                                          !competition.prices ||
                                          competition.prices.length === 0
                                        }
                                        icon={
                                          <CreditCardIcon className="w-6 h-6 mr-2" />
                                        }
                                      />
                                    )}
                                  </div>
                                )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {availableCompetitions.length > 0 && (
                  <div>
                    <div className="grid gap-6">
                      {availableCompetitions.map((competition) => (
                        <div
                          key={competition.id}
                          className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow"
                        >
                          <h3 className="text-xl font-bold mb-4 text-lime-600 flex items-center">
                            {competition.name}
                          </h3>

                          <div className="space-y-2 text-sm text-gray-600">
                            <p className="flex items-center">
                              <CalendarIcon className="w-4 h-4 mr-2" />
                              <strong className="mr-1">Inscripciones:</strong>
                              {
                                formatCompetitionDate(
                                  competition.registration_start
                                ).date
                              }{" "}
                              -{" "}
                              {
                                formatCompetitionDate(
                                  competition.registration_end
                                ).date
                              }
                            </p>
                            <p className="flex items-center">
                              <CalendarIcon className="w-4 h-4 mr-2" />
                              <strong className="mr-1">Competencia:</strong>
                              {
                                formatCompetitionDate(competition.start_date)
                                  .date
                              }{" "}
                              -{" "}
                              {formatCompetitionDate(competition.end_date).date}
                            </p>
                          </div>

                          {onRegister && (
                            <Button
                              onClick={() => onRegister(competition)}
                              text="Registrarse"
                              variant="create"
                              className="mt-4"
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        ) : (
          <Outlet />
        )}
      </div>
    </div>
  );
};
